/**
 * card.js — the standard list card factory.
 *
 * Builds the site's standard card (see the UI model): an optional left control
 * bar (icon / checkbox / spinner, stacked) + a main body with a title row
 * (primary line on the left, up to 3 horizontal icon-actions pinned top-right,
 * overflow into a kebab), an inline wrapping badge list, muted meta lines, and an
 * optional block-content region.
 *
 * The factory emits the existing `.card*` classes plus new `.card-control`
 * / `.card-badges` / `.card-content` slots (styles in card.css). Cards
 * built here carry a `.card` marker so the standard-model layout (e.g.
 * HORIZONTAL actions) applies only to factory cards, leaving legacy pages that
 * still hand-roll `.card` untouched until they migrate.
 *
 * Pure decision helpers (`splitActions`, `normaliseControl`) are exported for
 * unit testing; the DOM building itself is verified visually (consistent with the
 * other `components/*.js` DOM builders).
 */

import './card.css';
import { renderTagList } from './tags.js';
import { createKebabMenu } from './kebab-menu.js';

/** Max icon-actions shown inline before the rest collapse into a kebab menu. */
export const MAX_INLINE_ACTIONS = 3;

/** DOM-safe "is this a DOM node?" (the `Node` global is absent in the test env). */
function isNode(x) {
  return typeof Node !== 'undefined' && x instanceof Node;
}

/**
 * Decide which actions render inline vs. in the kebab.
 *
 * Rules:
 *  - An action with `kebab: true` is always forced into the overflow menu.
 *  - Of the remaining (inline-eligible) actions, if everything fits within
 *    MAX_INLINE_ACTIONS they all show inline and there is no kebab.
 *  - Otherwise we keep the first (MAX_INLINE_ACTIONS - 1) inline and move the
 *    rest to the kebab, so the kebab trigger itself counts toward the ≤3 icons.
 *
 * @param {Array<object>} actions
 * @returns {{ inline: object[], overflow: object[] }}
 */
export function splitActions(actions = []) {
  const forced = actions.filter((a) => a && a.kebab);
  const eligible = actions.filter((a) => a && !a.kebab);

  if (forced.length === 0 && eligible.length <= MAX_INLINE_ACTIONS) {
    return { inline: eligible, overflow: [] };
  }
  const inline = eligible.slice(0, MAX_INLINE_ACTIONS - 1);
  const overflow = [...eligible.slice(MAX_INLINE_ACTIONS - 1), ...forced];
  return { inline, overflow };
}

/**
 * Normalise a `meta` prop to an array of lines. Accepts null/undefined (none), a
 * single string/Node, or an array — so a caller passing a bare value or an
 * explicit `null` (which overrides the `= []` default) can't crash the factory.
 */
export function normaliseMeta(meta) {
  if (meta === null || meta === undefined) return [];
  return Array.isArray(meta) ? meta : [meta];
}

/**
 * Normalise the `control` option into an ordered list of control elements.
 * Accepts either a ready HTMLElement (used as-is) or an object of named controls
 * that are stacked in the fixed order icon → checkbox → spinner.
 *
 * @param {HTMLElement | {icon?: Node, checkbox?: Node, spinner?: Node} | null} control
 * @returns {Node[]} controls in render order (empty → no control bar)
 */
export function normaliseControl(control) {
  if (!control) return [];
  if (isNode(control)) return [control];
  const ordered = [];
  if (control.icon) ordered.push(control.icon);
  if (control.checkbox) ordered.push(control.checkbox);
  if (control.spinner) ordered.push(control.spinner);
  return ordered;
}

/**
 * Build an inline icon-action <button>.
 * `title` is the (succinct, generic) tooltip; `ariaLabel` is the (specific)
 * screen-reader label. `label` is a convenience fallback for either when only
 * one string is supplied.
 */
function buildActionButton({ icon, label, title, ariaLabel, onClick, danger, highlight }) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = danger
    ? 'icon-button icon-danger'
    : highlight
      ? 'icon-button icon-highlight'
      : 'icon-button';
  const tip = title ?? label;
  const aria = ariaLabel ?? label ?? title;
  if (tip) btn.title = tip;
  if (aria) btn.setAttribute('aria-label', aria);
  btn.innerHTML = `<i class="fa-solid ${icon}" aria-hidden="true"></i>`;
  if (onClick) btn.addEventListener('click', onClick);
  return btn;
}

/**
 * Build a standard card.
 *
 * @param {object} opts
 * @param {HTMLElement | {icon?, checkbox?, spinner?}} [opts.control] left bar
 *   (`icon` = a Node or an `fa-*` class string)
 * @param {string | Node} [opts.title] the card's primary line
 * @param {'heading'|'plain'} [opts.titleStyle='heading'] bold heading vs plain body
 * @param {boolean} [opts.truncateTitle=false] single-line ellipsis instead of wrap
 * @param {{icon: string, title?: string} | Node} [opts.titleIcon] a small muted
 *   `fa-*` icon after the title; `title` = its tooltip + a11y label. Or a
 *   pre-built Node (e.g. the notes hover hint from `notesTitleIcon`).
 * @param {Array<{icon, title?, ariaLabel?, label?, onClick?, danger?, highlight?, kebab?}>} [opts.actions]
 *   `highlight:true` draws an inline action in the brand colour (a suggested next step)
 *   `title` = succinct tooltip, `ariaLabel` = specific screen-reader label,
 *   `label` = fallback for either; `kebab:true` forces the item into the overflow menu
 * @param {string} [opts.kebabLabel='More actions'] a11y label for the kebab
 * @param {Array<string>|Node} [opts.badges] tag strings (→ renderTagList) or a node
 * @param {Array<string|Node>} [opts.meta] muted small lines
 * @param {Node} [opts.content] block-content region
 * @param {() => void} [opts.onClick] make the body a clickable button
 * @param {string} [opts.href] make the body a link (takes precedence over onClick)
 * @param {'button'|'checkbox'} [opts.activateRole='button'] a11y role of the body
 *   click target; 'checkbox' pairs with `ariaChecked` for toggle cards
 * @param {boolean} [opts.ariaChecked] initial `aria-checked` when role is checkbox
 * @param {Object<string,string>} [opts.dataset] data-* attributes on the row
 * @param {string} [opts.className] extra class(es) on the row
 * @returns {HTMLLIElement}
 */
export function createCard({
  control = null,
  title = '',
  titleStyle = 'heading',
  truncateTitle = false,
  titleIcon = null,
  actions = [],
  kebabLabel = 'More actions',
  badges = null,
  meta = [],
  content = null,
  onClick = null,
  href = null,
  activateRole = 'button',
  ariaChecked = null,
  dataset = null,
  className = '',
} = {}) {
  const row = document.createElement('li');
  row.className = 'card';
  if (className) row.className += ` ${className}`;
  if (dataset) for (const [k, v] of Object.entries(dataset)) row.dataset[k] = v;

  // Layout: the row is a flex container (control | body). The control bar is a
  // fixed-width flex COLUMN and the body is `flex:1` beside it — so a short
  // control leaves empty space below it (no text flows under the control). The
  // ONE float is the ACTIONS, right-floated INSIDE the body (contained by the
  // flex item), which is the only way to make the title/meta text wrap to their
  // left and under them. The whole-card click target is a `role="button"` div
  // (NOT a <button>/<a>, which couldn't let its own text wrap around the actions).

  // ── Left control bar (flex column, optional) ──
  // `control.icon` may be a Font Awesome class string (`'fa-briefcase'`), like
  // action icons, or a ready Node.
  if (control && !isNode(control) && typeof control.icon === 'string') {
    const i = document.createElement('i');
    i.className = `fa-solid ${control.icon}`;
    i.setAttribute('aria-hidden', 'true');
    control = { ...control, icon: i };
  }
  const controls = normaliseControl(control);
  if (controls.length) {
    const bar = document.createElement('div');
    bar.className = 'card-control';
    // A bar holding just an icon (no interactive checkbox/stepper) gets a small
    // top nudge so the icon lines up with the title (steppers/checkboxes don't).
    const iconOnly = !isNode(control) && control.icon && !control.checkbox && !control.spinner;
    if (iconOnly) bar.classList.add('card-control--icon');
    bar.append(...controls);
    // Control interactions must not also trigger the card's onClick.
    bar.addEventListener('click', (e) => e.stopPropagation());
    row.appendChild(bar);
  }

  // ── Body (block beside the control; contains the floated actions) ──
  const activate = href ? () => (window.location.href = href) : onClick;
  const body = document.createElement('div');
  body.className = 'card-body';
  if (activate) {
    // Only a card whose body actually does something on click gets the hover
    // affordance (background change); purely-informational cards (menu entry,
    // strategy, itinerary event) don't. Scoped via `.is-clickable` in CSS.
    row.classList.add('is-clickable');
    // `activateRole` picks the a11y role: 'button' (default) or 'checkbox' for
    // toggle cards (then `ariaChecked` reflects the state; callers update it).
    body.setAttribute('role', activateRole);
    body.tabIndex = 0;
    if (activateRole === 'checkbox') body.setAttribute('aria-checked', String(!!ariaChecked));
    if (typeof title === 'string' && title) body.setAttribute('aria-label', title);
    body.addEventListener('click', activate);
    body.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        activate();
      }
    });
  }

  // ── Actions (float:right INSIDE the body) — first in body source order so the
  // float pins them top-right and the title/meta wrap to their left/under. ──
  const { inline, overflow } = splitActions(actions);
  if (inline.length || overflow.length) {
    const actionsWrap = document.createElement('div');
    actionsWrap.className = 'card-actions';
    for (const a of inline) actionsWrap.appendChild(buildActionButton(a));
    if (overflow.length) {
      actionsWrap.appendChild(
        createKebabMenu({
          label: kebabLabel,
          items: overflow.map((a) => ({
            // Kebab rows show a visible text label → prefer the specific string.
            label: a.ariaLabel ?? a.label ?? a.title,
            icon: a.icon,
            danger: a.danger,
            onSelect: a.onClick,
          })),
        }),
      );
    }
    // Action clicks (and the kebab) must not also trigger the card's onClick.
    actionsWrap.addEventListener('click', (e) => e.stopPropagation());
    body.appendChild(actionsWrap);
  }

  // Title (primary line).
  if (title) {
    const titleEl = document.createElement('span');
    titleEl.className = 'card-title';
    if (titleStyle === 'plain') titleEl.classList.add('card-title--plain');
    if (truncateTitle) titleEl.classList.add('is-truncated');
    // With a `titleIcon`, the text gets its own span so a truncated title
    // ellipsises the TEXT and the icon stays visible after it.
    const textHost = titleIcon ? document.createElement('span') : titleEl;
    if (titleIcon) {
      textHost.className = 'card-title-text';
      titleEl.classList.add('has-icon');
    }
    if (isNode(title)) textHost.appendChild(title);
    else textHost.textContent = title;
    if (isNode(titleIcon)) {
      titleIcon.classList.add('card-title-icon');
      titleEl.append(textHost, titleIcon);
    } else if (titleIcon) {
      const i = document.createElement('i');
      i.className = `fa-solid ${titleIcon.icon} card-title-icon`;
      if (titleIcon.title) {
        i.title = titleIcon.title;
        i.setAttribute('role', 'img');
        i.setAttribute('aria-label', titleIcon.title);
      } else {
        i.setAttribute('aria-hidden', 'true');
      }
      titleEl.append(textHost, i);
    }
    body.appendChild(titleEl);
  }

  // Badges (inline wrapping list).
  if (badges) {
    if (isNode(badges)) {
      badges.classList.add('card-badges');
      body.appendChild(badges);
    } else {
      body.appendChild(renderTagList(badges, { className: 'card-badges' }));
    }
  }

  // Meta lines (muted, small).
  for (const line of normaliseMeta(meta)) {
    if (!line) continue;
    const m = document.createElement('span');
    m.className = 'card-meta';
    if (isNode(line)) m.appendChild(line);
    else m.textContent = line;
    body.appendChild(m);
  }

  // Block content.
  if (content) {
    const c = document.createElement('div');
    c.className = 'card-content';
    c.appendChild(content);
    body.appendChild(c);
  }

  row.appendChild(body);
  return row;
}
